<template>
  <div class="emp-settings-root">
    <!-- Sticky Header -->
    <div class="emp-header">
      <div class="d-flex align-center" style="gap: 10px">
        <div class="emp-header-icon">
          <v-icon small color="white">mdi-account-group</v-icon>
        </div>
        <div>
          <div class="text-subtitle-1 font-weight-bold white--text">Mitarbeiter-Einstellungen</div>
          <div class="text-caption" style="color: rgba(255, 255, 255, 0.4)">{{ monthName }} · {{ employees.length }} Mitarbeiter</div>
        </div>
      </div>
      <div class="d-flex align-center" style="gap: 12px">
        <v-chip small :color="readyCount === employees.length ? 'green' : 'orange'" text-color="white" class="font-weight-bold">
          {{ readyCount }}/{{ employees.length }} bereit
        </v-chip>
        <v-btn v-if="readyCount < employees.length" x-small text color="green accent-3" @click="markAllReady" class="text-none">Alle markieren</v-btn>
      </div>
    </div>

    <div class="emp-body">
      <div v-if="loading" class="emp-loading">
        <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
        <p class="mt-4" style="color: rgba(255, 255, 255, 0.4)">Lade Mitarbeiterdaten…</p>
      </div>

      <v-expansion-panels v-else accordion multiple v-model="openPanels" flat class="emp-panels">
        <v-expansion-panel v-for="(emp, index) in employees" :key="emp._id" class="emp-panel">
          <v-expansion-panel-header class="py-3">
            <template v-slot:default="{ open }">
              <v-row no-gutters align="center">
                <v-col cols="auto" class="mr-4">
                  <v-avatar size="48" class="elevation-2">
                    <img :src="emp.picture ? '/img/profilbilder' + emp.picture : '/img/default-avatar.png'" />
                  </v-avatar>
                </v-col>
                <v-col>
                  <div class="d-flex align-center">
                    <div class="text-h6 font-weight-bold white--text mr-2">{{ emp.fullName }}</div>

                    <v-tooltip right v-if="emp.specialAssignments && emp.specialAssignments.length > 0">
                      <template v-slot:activator="{ on, attrs }">
                        <v-chip x-small color="purple lighten-4" text-color="purple darken-3" class="font-weight-bold" v-bind="attrs" v-on="on">
                          <v-icon left x-small>mdi-star</v-icon> {{ emp.specialAssignments.length }}x Spezial
                        </v-chip>
                      </template>
                      <span>Mitarbeiter ist an {{ emp.specialAssignments.length }} Spezialtagen fix eingeteilt.</span>
                    </v-tooltip>
                  </div>

                  <div class="subtitle-2 white--text">
                    <span :class="emp.planningSettings.isReadyForPlanning ? 'success--text' : 'warning--text'">
                      {{ emp.planningSettings.isReadyForPlanning ? "Bereit" : "Noch nicht bestätigt" }}
                    </span>
                    <span class="mx-2">•</span>
                    Brutto-Soll: {{ formatHoursOrDash(calculateContractTarget(emp)) }}
                    <span class="mx-2">•</span>
                    Netto-Soll: {{ formatHoursOrDash(calculateShiftCapacity(emp)) }}
                  </div>
                </v-col>
                <v-col cols="auto" class="mr-4">
                  <v-chip v-if="emp.planningSettings.isReadyForPlanning" color="success" small label>
                    <v-icon left small>mdi-check</v-icon> Bereit
                  </v-chip>
                  <v-chip v-else color="orange" small label> Ausstehend </v-chip>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>

          <v-expansion-panel-content>
            <planning-settings-scope :key="emp._id" :value="emp._planningScope || 'month'" :month="planningMonthId"
              :settings="buildMonthlyPlanningSettings(emp)" :studio-settings="emp.shiftPlanningSettings && emp.shiftPlanningSettings[studioId] || {}"
              :disabled="emp._planningSettingsSaving === true" auto-save
              @input="setEmployeePlanningScope(emp, $event)" @adopt="adoptEmployeeStandardMonths(emp, $event)"></planning-settings-scope>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="6" class="pr-md-4 border-right-custom">
                <!-- Exit Warning & Overtime Compensation Widget -->
                <v-card
                  v-if="isExitInMonth(emp)"
                  class="pa-4 mb-4 rounded-xl"
                  outlined
                  style="background: rgba(255, 152, 0, 0.04); border: 1px solid rgba(255, 152, 0, 0.15) !important">
                  <div class="d-flex align-center mb-2" style="gap: 8px">
                    <v-icon color="orange" small>mdi-account-clock</v-icon>
                    <strong class="orange--text text-subtitle-2">Austritt mitten im Monat: {{ $moment(emp.exitDate).format("DD.MM.YYYY") }}</strong>
                  </div>

                  <div class="text-caption white--text opacity-semi mb-3">
                    Dieser Mitarbeiter verlässt das Center am {{ $moment(emp.exitDate).format("DD.MM.YYYY") }}. Die Sollstunden wurden automatisch
                    anteilig berechnet:
                  </div>

                  <div class="d-flex flex-column mb-3" style="gap: 6px">
                    <div class="d-flex justify-space-between text-caption">
                      <span class="grey--text">Brutto-Soll bis Austritt:</span>
                      <span class="white--text font-weight-medium">{{ formatHoursOrDash(calculateFullMonthSoll(emp)) }}</span>
                    </div>
                    <div class="d-flex justify-space-between text-caption">
                      <span class="grey--text">Netto-Soll nach Abzügen:</span>
                      <span class="orange--text font-weight-bold">{{ formatHoursOrDash(calculateCappedSoll(emp)) }}</span>
                    </div>
                    <div class="d-flex justify-space-between text-caption" v-if="emp.overTime !== undefined">
                      <span class="grey--text">Überstunden-Konto:</span>
                      <span :class="emp.overTime >= 0 ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
                        {{ emp.overTime >= 0 ? "+" : "" }}{{ emp.overTime.toFixed(1) }}h
                      </span>
                    </div>
                  </div>

                  <v-divider class="my-3" style="border-color: rgba(255, 152, 0, 0.1)"></v-divider>

                  <div class="d-flex align-center justify-space-between mb-2">
                    <div>
                      <span class="text-caption font-weight-bold white--text">Überstunden verrechnen?</span>
                      <div class="text-caption grey--text" style="font-size: 0.72rem !important; line-height: 1.2">
                        Zieht Überstunden von Sollstunden ab (bzw. rechnet Minusstunden hinzu)
                      </div>
                    </div>
                    <v-switch
                      v-model="emp.planningSettings.offsetOvertime"
                      color="orange"
                      hide-details
                      class="mt-0"
                      @change="toggleOvertimeOffset(emp)"></v-switch>
                  </div>

                  <v-expand-transition>
                    <div v-if="emp.planningSettings.offsetOvertime" class="mt-2">
                      <v-text-field
                        v-model.number="emp.planningSettings.customSollAdjustment"
                        label="Sollstunden-Korrektur"
                        type="number"
                        outlined
                        dense
                        hide-details
                        prefix="Sollstunden"
                        suffix="h"
                        hint="Negativ verringert die Sollstunden, positiv erhöht sie"
                        persistent-hint
                        @change="saveSettings(emp)"
                        class="mt-1"></v-text-field>
                    </div>
                  </v-expand-transition>
                </v-card>

                <v-card outlined class="pa-3 mb-4 d-flex align-center justify-space-between hover-card">
                  <div>
                    <div class="font-weight-bold">Planungs-Status</div>
                    <div class="text-caption white--text">Muss aktiv sein für Zuweisung</div>
                  </div>
                  <v-switch
                    v-model="emp.planningSettings.isReadyForPlanning"
                    color="success"
                    hide-details
                    class="mt-0"
                    @change="saveSettings(emp)"></v-switch>
                </v-card>

                <div class="mb-4 pa-3 rounded border">
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="font-weight-bold text-body-2">Mitarbeiter verstecken</div>
                      <div class="text-caption white--text">Auf Website und Listen ausblenden</div>
                    </div>
                    <v-switch
                      :input-value="getStudioInfo(emp).hide"
                      @change="(val) => updateStudioHideStatus(emp, val)"
                      dense
                      color="orange"
                      hide-details
                      class="mt-0"></v-switch>
                  </div>

                  <v-expand-transition>
                    <div v-if="getStudioInfo(emp).hide" class="pl-2 border-left-orange mt-2">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="font-weight-bold text-body-2">Im Kalender trotzdem anzeigen</div>
                          <div class="text-caption white--text">Trotzdem in Schichtplanung anzeigen</div>
                        </div>
                        <v-switch
                          :input-value="getStudioInfo(emp).showInCalendarAnyway"
                          @change="(val) => updateStudioShowInCalendarAnyway(emp, val)"
                          dense
                          color="green"
                          hide-details
                          class="mt-0"></v-switch>
                      </div>
                    </div>
                  </v-expand-transition>
                </div>

                <div class="mb-4 pa-2 rounded border">
                  <v-switch
                    v-model="emp.isApprentice"
                    label="Ist Lehrling / Auszubildender"
                    dense
                    color="blue darken-2"
                    hide-details
                    class="mt-0"
                    @change="updateEmployeeStatus(emp)"></v-switch>
                  <v-expand-transition>
                    <div v-if="emp.isApprentice" class="mt-3 pl-2 border-left-blue">
                      <div class="text-subtitle-2 blue--text text--darken-2 mb-1">Schultage</div>
                      <v-chip-group
                        multiple
                        column
                        active-class="blue white--text"
                        :value="getSchoolDaysAsArray(emp)"
                        @change="(val) => setSchoolDaysFromArray(emp, val)">
                        <v-chip small filter :value="1">Mo</v-chip>
                        <v-chip small filter :value="2">Di</v-chip>
                        <v-chip small filter :value="3">Mi</v-chip>
                        <v-chip small filter :value="4">Do</v-chip>
                        <v-chip small filter :value="5">Fr</v-chip>
                      </v-chip-group>
                      <v-divider class="my-2"></v-divider>
                      <v-switch
                        v-model="emp.planningSettings.longCommute"
                        label="Langer Schulweg"
                        dense
                        hide-details
                        color="blue"
                        @change="saveSettings(emp)"></v-switch>
                    </div>
                  </v-expand-transition>
                </div>

                <div class="mb-4">
                  <div class="d-flex justify-space-between align-end mb-1">
                    <span class="text-caption white--text font-weight-bold ml-1">
                      <v-icon x-small class="mr-1">mdi-file-document-outline</v-icon>
                      Stellenprozent (Statistik): {{ getStandardPercentage(emp) }}%
                    </span>
                  </div>
                  <v-text-field
                    label="Planungsprozent für diesen Monat"
                    v-model.number="emp.planningSettings.percentages[studioId]"
                    type="number"
                    outlined
                    dense
                    suffix="%"
                    prepend-inner-icon="mdi-chart-arc"
                    :hint="`Nur für ${planningMonthId}; leer = Statistik ${getStandardPercentage(emp)}%.`"
                    persistent-hint
                    @change="saveSettings(emp)"></v-text-field>

                  <v-expand-transition>
                    <div v-if="getStandardPercentage(emp) <= 40" class="mt-4">
                      <v-switch
                        v-model="emp.planningSettings.groupShifts"
                        label="Schichten in Blöcken gruppieren"
                        dense
                        hide-details
                        color="green"
                        hint="Kleine Pensen: Mitarbeiter nach Möglichkeit an aufeinanderfolgenden Tagen einteilen"
                        persistent-hint
                        @change="saveSettings(emp)"></v-switch>
                    </div>
                  </v-expand-transition>
                </div>

                <!-- Team-Präferenzen (Preferred Partners) -->
                <div class="mt-4 mb-2">
                  <div class="text-subtitle-2 font-weight-bold mb-2 d-flex align-center">
                    <v-icon left color="pink" small>mdi-account-heart</v-icon> Team-Präferenzen
                  </div>
                  <v-autocomplete
                    v-model="emp.planningSettings.preferredPartners"
                    :items="otherEmployees(emp)"
                    item-text="fullName"
                    item-value="userId"
                    label="Bevorzugte Teampartner"
                    multiple
                    outlined
                    dense
                    chips
                    small-chips
                    deletable-chips
                    hide-details
                    placeholder="Mitarbeiter auswählen..."
                    color="pink"
                    @change="saveSettings(emp)"></v-autocomplete>
                </div>

                <div class="mt-6">
                  <div class="text-subtitle-1 font-weight-bold mb-1 d-flex align-center">
                    <v-icon left color="primary">mdi-pin</v-icon> Definitive Zuweisungen
                  </div>
                  <div v-for="day in 7" :key="day" class="d-flex align-center mb-2">
                    <div class="font-weight-medium mr-3" style="width: 100px">{{ getDayName(day) }}</div>
                    <v-select
                      :items="availableShifts"
                      v-model="emp.planningSettings.fixedShifts[day]"
                      item-text="name"
                      item-value="_id"
                      label="Automatisch"
                      dense
                      outlined
                      small-chips
                      clearable
                      hide-details
                      single-line
                      color="primary"
                      placeholder="Keine Vorgabe"
                      @change="saveSettings(emp)">
                      <template v-slot:item="{ item }">
                        <v-chip x-small :color="item.color" class="mr-2"></v-chip>
                        {{ item.name }}
                      </template>
                      <template v-slot:selection="{ item }">
                        <v-chip x-small :color="item.color" class="mr-2"></v-chip>
                        {{ item.name }}
                      </template>
                    </v-select>
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6" class="pl-md-4">
                <div class="text-subtitle-1 font-weight-bold mb-3 d-flex align-center">
                  <v-icon left>mdi-calendar-account</v-icon> Abwesenheiten & Wünsche
                </div>

                <v-card outlined class="overflow-hidden">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead class="">
                        <tr>
                          <th class="text-left font-weight-bold">Art</th>
                          <th class="text-left font-weight-bold">Datum</th>
                          <th class="text-left font-weight-bold">Info</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(spec, i) in emp.specialAssignments" :key="'spec' + i" class="hover-row">
                          <td>
                            <v-chip small color="purple lighten-4" text-color="purple darken-4" label class="font-weight-bold">
                              <v-icon left x-small>mdi-star</v-icon> Spezial
                            </v-chip>
                          </td>
                          <td class="font-weight-bold">{{ $moment(spec.date).format("dd, DD.MM") }}</td>
                          <td>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <div v-bind="attrs" v-on="on" class="d-flex align-center cursor-help">
                                  <v-icon small color="purple" class="mr-1">mdi-clock-time-four-outline</v-icon>
                                  <span class="text-caption font-weight-bold purple--text text--darken-2">Fixiert</span>
                                </div>
                              </template>
                              <div class="text-center">
                                <strong>{{ spec.note || "Spezial-Einsatz" }}</strong
                                ><br />
                                <div v-for="(t, ti) in spec.times" :key="ti">{{ t.start }} - {{ t.end }} ({{ t.type }})</div>
                              </div>
                            </v-tooltip>
                          </td>
                        </tr>

                        <tr v-for="(vac, i) in emp.vacations" :key="'vac' + i" class="hover-row">
                          <td>
                            <v-chip small color="blue lighten-4" text-color="blue darken-4" label
                              ><v-icon left x-small>mdi-beach</v-icon> Ferien</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ vac.formattedRange }}</td>
                          <td class="success--text text-caption font-weight-bold"><v-icon x-small color="success">mdi-check</v-icon> Bestätigt</td>
                        </tr>

                        <tr v-for="(fw, i) in emp.freeWishes" :key="'fw' + i" class="hover-row">
                          <td>
                            <v-chip small color="red lighten-4" text-color="red darken-4" label
                              ><v-icon left x-small>mdi-close-circle</v-icon> Frei</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ $moment(fw.day).format("dd, DD.MM") }}</td>
                          <td class="text-caption">Ganzer Tag</td>
                        </tr>

                        <tr v-for="(sw, i) in emp.shiftWishes" :key="'sw' + i" class="hover-row">
                          <td>
                            <v-chip small color="green lighten-4" text-color="green darken-4" label
                              ><v-icon left x-small>mdi-clock-outline</v-icon> Wunsch</v-chip
                            >
                          </td>
                          <td class="font-weight-medium">{{ $moment(sw.day).format("dd, DD.MM") }}</td>
                          <td class="text-caption font-weight-bold">{{ getShiftTypeName(sw.type) }}</td>
                        </tr>

                        <!-- NEU: Abwesenheiten (Krank, Militär, etc.) -->
                        <tr v-for="(abs, i) in emp.absences" :key="'abs' + i" class="hover-row">
                          <td>
                            <v-chip small :color="abs.color + ' lighten-4'" :text-color="abs.color + ' darken-4'" label>
                              <v-icon left x-small>{{ abs.icon }}</v-icon> {{ abs.label }}
                            </v-chip>
                          </td>
                          <td class="font-weight-medium">
                            <div>{{ abs.formattedRange }}</div>
                            <div
                              v-if="abs.sickPercentage < 100"
                              class="orange--text text-caption font-weight-bold"
                              style="font-size: 0.72rem !important; margin-top: 1px">
                              {{ abs.sickPercentage }}% {{ abs.absenceReason === "sick" ? "krank" : "abwesend" }}
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <span
                                :class="
                                  abs.status === 'accepted'
                                    ? 'success--text text-caption font-weight-bold'
                                    : 'orange--text text-caption font-weight-bold'
                                ">
                                <v-icon x-small :color="abs.status === 'accepted' ? 'success' : 'orange'" class="mr-1">
                                  {{ abs.status === "accepted" ? "mdi-check" : "mdi-clock-outline" }}
                                </v-icon>
                                {{ abs.status === "accepted" ? "Bestätigt" : "Ausstehend" }}
                              </span>
                              <span v-if="abs.note" class="grey--text text-caption mt-1" style="font-size: 0.72rem !important; line-height: 1.2">
                                {{ abs.note }}
                              </span>
                            </div>
                          </td>
                        </tr>

                        <tr
                          v-if="
                            (!emp.vacations || !emp.vacations.length) &&
                            (!emp.freeWishes || !emp.freeWishes.length) &&
                            (!emp.shiftWishes || !emp.shiftWishes.length) &&
                            (!emp.specialAssignments || !emp.specialAssignments.length) &&
                            (!emp.absences || !emp.absences.length)
                          ">
                          <td colspan="3" class="text-center white--text py-6">
                            <v-icon color="grey lighten-1" large>mdi-calendar-check</v-icon>
                            <div class="mt-2">Keine Einträge diesen Monat</div>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card>

                <div class="mt-6">
                  <div class="text-subtitle-2 mb-2 white--text text--darken-2">Wiederkehrende Sperrtage</div>
                  <v-chip-group multiple v-model="emp.planningSettings.recurringAbsence" column @change="saveSettings(emp)">
                    <v-chip filter outlined color="error" :value="1">Mo</v-chip>
                    <v-chip filter outlined color="error" :value="2">Di</v-chip>
                    <v-chip filter outlined color="error" :value="3">Mi</v-chip>
                    <v-chip filter outlined color="error" :value="4">Do</v-chip>
                    <v-chip filter outlined color="error" :value="5">Fr</v-chip>
                    <v-chip filter outlined color="error" :value="6">Sa</v-chip>
                    <v-chip filter outlined color="error" :value="7">So</v-chip>
                  </v-chip-group>
                </div>

                <div class="mt-6">
                  <div class="text-subtitle-2 mb-1 blue--text text--lighten-2">Regelmässige Tage in anderem Studio</div>
                  <div class="caption grey--text mb-2">
                    Sperrt diese Tage für automatische Vorschläge. Sollstunden werden nur durch tatsächlich geplante Fremdstudio-Schichten aufgeteilt.
                  </div>
                  <v-chip-group multiple v-model="emp.planningSettings.recurringExternalStudio" column @change="saveSettings(emp)">
                    <v-chip filter outlined color="blue" :value="1">Mo</v-chip>
                    <v-chip filter outlined color="blue" :value="2">Di</v-chip>
                    <v-chip filter outlined color="blue" :value="3">Mi</v-chip>
                    <v-chip filter outlined color="blue" :value="4">Do</v-chip>
                    <v-chip filter outlined color="blue" :value="5">Fr</v-chip>
                    <v-chip filter outlined color="blue" :value="6">Sa</v-chip>
                    <v-chip filter outlined color="blue" :value="7">So</v-chip>
                  </v-chip-group>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!-- Bottom Bar -->
    <div class="emp-bottom">
      <div class="emp-bottom-inner">
        <!-- Coverage Bar -->
        <div class="emp-coverage" @click="showCalcDialog = true">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-caption font-weight-bold" style="color: rgba(255, 255, 255, 0.5)"> Abdeckung </span>
            <span class="text-caption font-weight-bold" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
              {{ Math.round(totalSupplyHours) }}h / {{ Math.round(totalDemandHours) }}h ({{ hourDiff > 0 ? "+" : "" }}{{ Math.round(hourDiff) }}h)
              <v-icon x-small color="grey" class="ml-1">mdi-information-outline</v-icon>
            </span>
          </div>
          <div class="emp-progress-wrap">
            <div class="emp-progress-bg">
              <div
                class="emp-progress-fill"
                :class="coveragePercentage >= 100 ? 'complete' : coveragePercentage >= 80 ? 'partial' : 'low'"
                :style="{ width: Math.min(coveragePercentage, 100) + '%' }"></div>
            </div>
            <span
              class="text-caption font-weight-bold ml-2"
              :class="coveragePercentage >= 100 ? 'success--text' : 'warning--text'"
              style="min-width: 36px"
              >{{ Math.round(coveragePercentage) }}%</span
            >
          </div>
        </div>

        <v-btn depressed @click="triggerAssignment" :disabled="loading || readyCount === 0 || !hasCompleteWorkHoursSummaries" class="emp-submit-btn">
          <v-icon left small>mdi-auto-fix</v-icon> Plan erstellen
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="showCalcDialog" max-width="700">
      <v-card>
        <v-card-title>
          Bedarfs-Berechnung
          <v-spacer></v-spacer>
          <v-btn icon @click="showCalcDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6" class="border-right-custom">
              <div class="text-h6 mb-2">Bedarf (Soll)</div>
              <div class="text-h4 font-weight-bold mb-4">{{ Math.round(totalDemandHours) }}h</div>

              <v-simple-table dense>
                <tbody>
                  <tr>
                    <td>Standard-Monat</td>
                    <td class="text-right">{{ calcStats.standardHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.removedHours > 0">
                    <td class="orange--text text--darken-2">Entfall durch Events (Override)</td>
                    <td class="text-right orange--text text--darken-2">- {{ calcStats.removedHours.toFixed(1) }}h</td>
                  </tr>
                  <tr v-if="calcStats.specialHours > 0">
                    <td class="blue--text text--darken-2">Spezial- & Zusatzschichten</td>
                    <td class="text-right blue--text text--darken-2">+ {{ calcStats.specialHours.toFixed(1) }}h</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div v-if="calcStats.specialEvents.length > 0" class="mt-4">
                <div class="text-caption font-weight-bold white--text mb-1">GEPLANTE SPEZIAL-EVENTS</div>
                <div v-for="(evt, i) in calcStats.specialEvents" :key="i" class="d-flex justify-space-between text-caption mb-1">
                  <span>{{ $moment(evt.date).format("DD.MM") }}: {{ evt.shifts }} Schichten</span>
                  <v-chip x-small label :color="evt.mode === 'replace' ? 'orange lighten-4' : 'blue lighten-4'">
                    {{ evt.mode === "replace" ? "Ersatz" : "Zusatz" }}
                  </v-chip>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-h6 mb-2">Kapazität (Haben)</div>
              <div class="text-h4 font-weight-bold mb-4" :class="hourDiff >= 0 ? 'success--text' : 'error--text'">
                {{ Math.round(totalSupplyHours) }}h
              </div>
              <div class="text-body-2 white--text mb-2">Summe aller Mitarbeiter-Sollstunden abzüglich Schule.</div>
              <v-divider class="mb-2"></v-divider>
              <div class="d-flex justify-space-between text-body-2">
                <span>Differenz:</span>
                <strong :class="hourDiff >= 0 ? 'success--text' : 'error--text'"> {{ hourDiff > 0 ? "+" : "" }}{{ Math.round(hourDiff) }}h </strong>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { resolvePlanningSettings, standardUpdateRequest } from "./planningStandards"
import PlanningSettingsScope from "./PlanningSettingsScope.vue"
import api from "@/api"

export default {
  name: "ShiftManagementEmployeeSettings",
  components: { PlanningSettingsScope },
  props: {
    studioId: { type: String, required: true },
    planningStartDate: { type: String, required: true },
    planningEndDate: { type: String, required: true },
    weeklyTemplateData: { type: Array, default: () => [] },
    availableShifts: { type: Array, default: () => [] },
    schoolHolidays: { type: Array, default: () => [] },
    confirmedEmployeeSettings: { type: Array, default: () => [] },
    handleAssignmentComplete: { type: Function, default: null },
  },
  data() {
    return {
      employees: [],
      specialShifts: [],
      publicHolidays: [], // NEU: Feiertage
      workHoursSummaryWarning: "",
      workHoursSummaryLoading: false,
      workHoursSummaryRequestIds: {},
      workHoursSummaryLoadId: 0,
      workHoursSummaryTimeoutMs: 12000,
      loading: false,
      assigning: false,
      openPanels: [],
      totalDemandHours: 0,
      showCalcDialog: false,
      calcStats: {
        standardHours: 0,
        removedHours: 0,
        specialHours: 0,
        specialEvents: [],
      },
    }
  },
  computed: {
    monthName() {
      return this.planningStartDate ? this.$moment(this.planningStartDate).format("MMMM YYYY") : ""
    },
    readyCount() {
      return this.employees.filter((e) => e.planningSettings.isReadyForPlanning).length
    },
    planningMonthId() {
      return this.planningStartDate ? this.$moment(this.planningStartDate).format("YYYY-MM") : ""
    },
    hasCompleteWorkHoursSummaries() {
      if (this.workHoursSummaryLoading) return false
      const relevantEmployees = this.employees.filter((employee) => employee?.planningSettings?.isReadyForPlanning === true)
      if (relevantEmployees.length === 0) return false
      return relevantEmployees.every(
        (employee) =>
          employee._monthlySettingsSaveFailed !== true &&
          employee._planningSettingsSaving !== true &&
          employee.workHoursSummaryUnavailable !== true &&
          this.isValidWorkHoursSummary(employee.hoursSummary),
      )
    },
    // NEU: Feiertage die Mo-Fr sind (relevant für Sollstunden)
    publicHolidaysInRange() {
      return this.publicHolidays.filter((h) => !h.isWeekend)
    },
    totalSupplyHours() {
      return this.employees.reduce((sum, emp) => {
        if (!emp.planningSettings.isReadyForPlanning) return sum
        return sum + this.calculateShiftCapacity(emp)
      }, 0)
    },
    hourDiff() {
      return this.totalSupplyHours - this.totalDemandHours
    },
    coveragePercentage() {
      if (this.totalDemandHours === 0) return 100
      const p = (this.totalSupplyHours / this.totalDemandHours) * 100
      return p > 100 ? 100 : p
    },
    coverageColor() {
      if (this.coveragePercentage < 80) return "error"
      if (this.coveragePercentage < 100) return "warning"
      return "success"
    },
  },
  watch: {
    studioId: { immediate: true, handler: "fetchDataAndCalculateTarget" },
    weeklyTemplateData: { immediate: true, handler: "calculateDemand" },
  },
  methods: {
    withTimeout(promise, timeoutMs, message) {
      let timeoutId
      const timeout = new Promise((resolve, reject) => {
        timeoutId = setTimeout(() => reject(new Error(message || "Zeitüberschreitung")), timeoutMs)
      })
      return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId))
    },
    getEffectiveMonthSettings(employee) {
      const studioSettings = employee?.shiftPlanningSettings?.[this.studioId] || {}
      return resolvePlanningSettings(studioSettings, this.planningMonthId)
    },
    isValidWorkHoursSummary(summary) {
      const total = summary && summary.total
      if (!total) return false
      const hasNumber = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
      return (
        hasNumber(summary.contractPercentage) &&
        hasNumber(summary.targetWorkdays) &&
        hasNumber(total.grossTargetHours) &&
        hasNumber(total.netTargetHours) &&
        hasNumber(total.plannedHours)
      )
    },
    applyWorkHoursSummaries(summaries) {
      let missing = 0
      this.employees.forEach((employee) => {
        const employeeUserId = String(employee?.user?._id || employee?.user || "")
        const summary = summaries?.[employeeUserId]
        const valid = this.isValidWorkHoursSummary(summary)
        this.$set(employee, "hoursSummary", valid ? summary : null)
        this.$set(employee, "sollStunden", valid ? Number(summary.total.netTargetHours) : null)
        this.$set(employee, "plannedHours", valid ? Number(summary.total.plannedHours) : null)
        this.$set(employee, "workHoursSummaryUnavailable", !valid)
        if (!valid) missing += 1
      })
      this.employees = [...this.employees]
      return missing
    },
    async loadAllWorkHoursSummaries({ notify = false } = {}) {
      if (!this.studioId || !this.planningStartDate || !this.planningEndDate) return false
      const loadId = ++this.workHoursSummaryLoadId
      const studioId = String(this.studioId)
      const monthId = String(this.planningMonthId)
      this.workHoursSummaryLoading = true
      this.workHoursSummaryWarning = ""
      try {
        const response = await this.withTimeout(
          api.shiftManagement.fetchWorkHoursSummaries([studioId], this.planningStartDate, this.planningEndDate),
          this.workHoursSummaryTimeoutMs,
          "Die Sollstunden-Berechnung hat zu lange gedauert.",
        )
        if (loadId !== this.workHoursSummaryLoadId || String(this.studioId) !== studioId || String(this.planningMonthId) !== monthId) return false
        const summaries = response?.summaries
        if (!summaries || typeof summaries !== "object") throw new Error("Zentrale Stundenübersicht fehlt.")
        const missing = this.applyWorkHoursSummaries(summaries)
        const relevantMissing = this.employees.filter(
          (employee) =>
            employee?.planningSettings?.isReadyForPlanning === true &&
            (employee._monthlySettingsSaveFailed === true ||
              employee.workHoursSummaryUnavailable === true ||
              !this.isValidWorkHoursSummary(employee.hoursSummary)),
        ).length
        if (relevantMissing > 0) {
          this.workHoursSummaryWarning =
            `${relevantMissing} aktive Person${relevantMissing === 1 ? "" : "en"} hat unvollständige Monatswerte oder Stundenübersichten. ` +
            "Die Einstellungen bleiben sichtbar; die automatische Zuweisung ist bis zum erfolgreichen erneuten Speichern und Berechnen gesperrt."
          return false
        }
        if (missing > 0) {
          this.workHoursSummaryWarning =
            `${missing} inaktive Stundenübersicht${missing === 1 ? "" : "en"} fehlt. ` +
            "Die automatische Zuweisung der vollständig berechneten aktiven Personen bleibt möglich."
          return true
        }
        this.workHoursSummaryWarning = ""
        if (notify) this.$toast.success("Sollstunden wurden neu berechnet.")
        return true
      } catch (error) {
        if (loadId !== this.workHoursSummaryLoadId) return false
        console.warn("Zentrale Stundenübersicht im Monats-Wizard nicht verfügbar:", error)
        this.applyWorkHoursSummaries({})
        this.workHoursSummaryWarning =
          "Die Sollstunden konnten nicht zuverlässig berechnet werden. Die Einstellungen bleiben sichtbar; die automatische Zuweisung ist gesperrt."
        return false
      } finally {
        if (loadId === this.workHoursSummaryLoadId) this.workHoursSummaryLoading = false
      }
    },
    async retryWorkHoursSummaries() {
      if (this.workHoursSummaryLoading) return
      const failedSaves = this.employees.filter((employee) => employee._monthlySettingsSaveFailed === true)
      if (failedSaves.length > 0) {
        await Promise.all(failedSaves.map((employee) => this.saveSettings(employee)))
      }
      await this.loadAllWorkHoursSummaries({ notify: true })
    },
    // NEU: Spezialtage beim Laden mit aufbereiten
    async fetchDataAndCalculateTarget() {
      if (!this.studioId || !this.planningStartDate || !this.planningEndDate) return

      this.loading = true
      this.workHoursSummaryWarning = ""
      this.workHoursSummaryLoading = true
      try {
        const [allEmployees, planRes, publicHolidaysRes] = await Promise.all([
          api.shiftManagement.fetchShiftManagementEmployee(this.studioId),
          api.shiftManagement.fetchPlan(this.studioId, this.planningStartDate, this.planningEndDate, true),
          api.shiftManagement.fetchPublicHolidays(this.studioId, this.planningStartDate, this.planningEndDate),
        ])

        // Feiertage speichern
        this.publicHolidays = publicHolidaysRes || []

        // Spezialschichten laden
        const allShifts = planRes.shifts || planRes || []
        // Filtern nach Spezial
        this.specialShifts = allShifts.filter((s) => s.isSpecialDemand === true || (s.allocationNote && s.allocationNote.includes("Spezialtag")))

        this.employees = allEmployees
          .filter((emp) => {
            const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
            // Auch 0%-Mitarbeitende müssen sichtbar bleiben: Sie können für
            // das Studio berechtigt sein, ohne dort eigene Sollstunden zu haben.
            return Boolean(studioInfo && (studioInfo.hide !== true || studioInfo.showInCalendarAnyway === true))
          })
          .map((emp) => {
            // Gleiche effektive Basis+Monatswerte wie beim bestehenden Monat.
            const backendSettings = this.getEffectiveMonthSettings(emp)
            // Fallback: localStorage für Migration
            const storageKey = `planningSettings_${emp._id}_${this.studioId}`
            let localSettings = {}
            try {
              localSettings = JSON.parse(localStorage.getItem(storageKey)) || {}
            } catch (error) {
              console.warn(`Ungültige lokale Planungseinstellungen für ${emp.fullName || emp._id} werden ignoriert:`, error)
            }
            // Backend hat Priorität, Fallback auf localStorage
            const savedSettings =
              Object.keys(backendSettings).length > 0
                ? backendSettings
                : {
                    ...localSettings,
                    // Der alte LocalStorage-Key ist nicht monatsbezogen und
                    // darf deshalb nie ein Planungsprozent liefern.
                    percentages: {},
                  }

            const planningSettings = {
              percentages: savedSettings.percentages || {},
              recurringAbsence: savedSettings.recurringAbsence || [],
              recurringExternalStudio: savedSettings.recurringExternalStudio || [],
              isReadyForPlanning: savedSettings.isReadyForPlanning === true,
              longCommute: savedSettings.longCommute || false,
              fixedShifts: savedSettings.fixedShifts || {},
              preferredPartners: savedSettings.preferredPartners || [],
              customSollAdjustment: savedSettings.customSollAdjustment || 0,
              offsetOvertime: savedSettings.offsetOvertime === true,
              groupShifts: savedSettings.groupShifts === true,
              isApprentice: typeof savedSettings.isApprentice === "boolean" ? savedSettings.isApprentice : emp.isApprentice === true,
              schoolDays: Array.isArray(savedSettings.schoolDays) ? savedSettings.schoolDays : emp.schoolDays || [],
            }

            const vacations = this.filterByDate(emp.holidayEntries, "dates")
            const freeWishes = this.filterByDateSingle(emp.freeWishEntries, "day")
            const shiftWishes = this.filterByDateSingle(emp.shiftWishEntries, "day")

            // NEU: Spezialschichten für diesen Mitarbeiter sammeln
            const specialAssignments = this.specialShifts
              .filter((s) => {
                const assignedId = s.assignedEmployee ? s.assignedEmployee._id || s.assignedEmployee : null
                // Sicherstellen dass wir IDs vergleichen
                const empUserId = emp.user._id || emp.user
                return String(assignedId) === String(empUserId)
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date))

            // Abwesenheiten filtern und für Tabelle mappen
            const rawAbsences = (emp.sickEntries || []).filter((abs) => {
              if (abs.status === "rejected") return false
              const dates = abs.sickDays
              if (!dates || !dates.length) return false
              const start = this.$moment(dates[0].date)
              const end = this.$moment(dates[dates.length - 1].date)
              return start.isSameOrBefore(this.planningEndDate) && end.isSameOrAfter(this.planningStartDate)
            })

            const absencesMapped = rawAbsences.map((abs) => {
              const sortedDays = [...abs.sickDays].sort((a, b) => a.date.localeCompare(b.date))
              const formattedRange =
                sortedDays.length > 1
                  ? `${this.$moment(sortedDays[0].date).format("DD.MM")} - ${this.$moment(sortedDays[sortedDays.length - 1].date).format("DD.MM")}`
                  : this.$moment(sortedDays[0].date).format("dd, DD.MM")

              const reasonLabels = {
                sick: "Krank",
                school: "Schule",
                military: "Militär",
                pregnancy: "Schwangerschaft / Mutterschaft",
                other: "Anderes",
              }
              const reasonColors = {
                sick: "red",
                school: "blue",
                military: "purple",
                pregnancy: "teal",
                other: "grey",
              }
              const reasonIcons = {
                sick: "mdi-emoticon-sad",
                school: "mdi-school",
                military: "mdi-shield",
                pregnancy: "mdi-baby-carriage",
                other: "mdi-alert-circle-outline",
              }

              let percentage = 100
              if (abs.sickDays) {
                const partialDay = abs.sickDays.find((d) => {
                  let pct = d.sickPercentage
                  if (pct === undefined) {
                    pct = d.percentage !== undefined ? d.percentage : 100
                  }
                  return pct < 100
                })
                if (partialDay) {
                  percentage = partialDay.sickPercentage !== undefined ? partialDay.sickPercentage : partialDay.percentage
                }
              }

              return {
                ...abs,
                label: reasonLabels[abs.absenceReason] || "Abwesenheit",
                color: reasonColors[abs.absenceReason] || "grey",
                icon: reasonIcons[abs.absenceReason] || "mdi-alert-circle-outline",
                formattedRange,
                sickPercentage: percentage,
              }
            })

            return {
              ...emp,
              _profilePlanningStatus: {
                isApprentice: emp.isApprentice === true,
                schoolDays: Array.isArray(emp.schoolDays) ? emp.schoolDays : [],
                cannotOpenOrCloseShift: emp.cannotOpenOrCloseShift === true,
              },
              isApprentice: planningSettings.isApprentice,
              schoolDays: planningSettings.schoolDays,
              vacations: vacations.map((h) => ({
                ...h,
                formattedRange:
                  h.dates && h.dates.length
                    ? `${this.$moment(h.dates[0].date).format("DD.MM")} - ${this.$moment(h.dates[h.dates.length - 1].date).format("DD.MM")}`
                    : "",
              })),
              freeWishes,
              shiftWishes,
              specialAssignments, // <--- Hinzugefügt
              absences: absencesMapped,
              planningSettings,
              hoursSummary: null,
              sollStunden: null,
              plannedHours: null,
              workHoursSummaryUnavailable: true,
            }
          })

        this.employees.forEach((employee) => {
          employee._workHoursSummaryInputSignature = this.getWorkHoursSummaryInputSignature(employee)
        })
        this.calculateDemand()
        // Die fachliche Summary läuft separat und zeitbegrenzt. Die
        // Mitarbeiteransicht bleibt sichtbar; nur die Automatik wartet darauf.
        void this.loadAllWorkHoursSummaries()
      } catch (error) {
        console.error("Fehler beim Laden:", error)
        this.$toast.error("Daten konnten nicht vollständig geladen werden.")
        this.workHoursSummaryLoading = false
      } finally {
        this.loading = false
      }
    },

    getDayName(iso) {
      const days = ["-", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"]
      return days[iso]
    },
    getShiftTypeName(type) {
      const map = { morning: "Früh", intermediate: "Mittel", late: "Spät" }
      return map[type] || type
    },
    getStandardPercentage(emp) {
      if (!emp) return 0
      const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
      if (studioInfo?.percentage === undefined || studioInfo?.percentage === null || studioInfo?.percentage === "") return 0
      const percentage = Number(String(studioInfo.percentage).replace(",", "."))
      return Number.isFinite(percentage) ? Math.max(0, percentage) : 0
    },

    getSchoolDaysAsArray(emp) {
      if (!emp.schoolDays) return []
      return emp.schoolDays.map((schoolDay) => Number(schoolDay?.dayOfWeek ?? schoolDay)).filter((dayOfWeek) => Number.isFinite(dayOfWeek))
    },
    setSchoolDaysFromArray(emp, valArray) {
      emp.schoolDays = valArray.map((day) => ({ dayOfWeek: day }))
      this.$set(emp.planningSettings, "schoolDays", emp.schoolDays)
      this.updateEmployeeStatus(emp)
    },

    // ... (Filter Methoden bleiben gleich) ...
    filterByDate(entries, dateFieldArrayName) {
      if (!entries) return []
      return entries.filter((e) => {
        if (e.status && e.status !== "accepted") return false
        const dates = e[dateFieldArrayName]
        if (!dates || !dates.length) return false
        const start = this.$moment(dates[0].date)
        const end = this.$moment(dates[dates.length - 1].date)
        return start.isSameOrBefore(this.planningEndDate) && end.isSameOrAfter(this.planningStartDate)
      })
    },
    filterByDateSingle(entries, dateFieldName) {
      if (!entries) return []
      return entries.filter((e) => {
        if (e.status && e.status !== "accepted") return false
        const date = this.$moment(e[dateFieldName])
        return date.isBetween(this.planningStartDate, this.planningEndDate, null, "[]")
      })
    },

    getWorkHoursSummaryInputSignature(emp) {
      const settings = emp?.planningSettings || {}
      const percentage = settings.percentages ? settings.percentages[String(this.studioId)] : null
      const schoolDays = (emp?.schoolDays || [])
        .map((day) => Number(day?.dayOfWeek ?? day))
        .filter((day) => Number.isFinite(day))
        .sort((left, right) => left - right)
      return JSON.stringify({
        percentage: percentage === "" ? null : percentage,
        customSollAdjustment: Number(settings.customSollAdjustment || 0),
        isApprentice: emp?.isApprentice === true,
        schoolDays,
      })
    },

    async refreshEmployeeWorkHoursSummary(emp) {
      const employeeUserId = String(emp?.user?._id || emp?.user || "")
      if (!employeeUserId) return
      const requestId = Number(this.workHoursSummaryRequestIds[employeeUserId] || 0) + 1
      this.$set(this.workHoursSummaryRequestIds, employeeUserId, requestId)
      this.$set(emp, "workHoursSummaryUnavailable", true)

      const override = {
        ...this.buildMonthlyPlanningSettings(emp),
      }
      try {
        const response = await this.withTimeout(
          api.shiftManagement.fetchWorkHoursSummaries([this.studioId], this.planningStartDate, this.planningEndDate, { [employeeUserId]: override }),
          this.workHoursSummaryTimeoutMs,
          "Die Sollstunden-Berechnung hat zu lange gedauert.",
        )
        const summary = response?.summaries?.[employeeUserId]
        if (!this.isValidWorkHoursSummary(summary)) throw new Error("Zentrale Stundenübersicht fehlt oder ist unvollständig.")
        if (this.workHoursSummaryRequestIds[employeeUserId] !== requestId) return

        this.$set(emp, "hoursSummary", summary)
        this.$set(emp, "sollStunden", Number(summary.total.netTargetHours) || 0)
        this.$set(emp, "plannedHours", Number(summary.total.plannedHours) || 0)
        this.$set(emp, "workHoursSummaryUnavailable", false)
        emp._workHoursSummaryInputSignature = this.getWorkHoursSummaryInputSignature(emp)

        if (this.employees.every((employee) => this.isValidWorkHoursSummary(employee.hoursSummary))) {
          this.workHoursSummaryWarning = ""
        }
      } catch (error) {
        if (this.workHoursSummaryRequestIds[employeeUserId] !== requestId) return
        console.warn(`Stundenübersicht für ${emp.fullName || employeeUserId} konnte nicht aktualisiert werden:`, error)
        this.$set(emp, "workHoursSummaryUnavailable", true)
        this.workHoursSummaryWarning =
          "Die Sollstunden-Berechnung konnte nicht aktualisiert werden. Die Einstellungen bleiben gespeichert; die automatische Zuweisung ist gesperrt."
      }
    },

    refreshEmployeeWorkHoursSummaryIfNeeded(emp) {
      if (!emp) return
      const nextSignature = this.getWorkHoursSummaryInputSignature(emp)
      if (nextSignature === emp._workHoursSummaryInputSignature) return
      void this.refreshEmployeeWorkHoursSummary(emp)
    },

    buildMonthlyPlanningSettings(emp) {
      const fixedShifts = {}
      for (let day = 1; day <= 7; day++) fixedShifts[day] = emp?.planningSettings?.fixedShifts?.[day] || null
      return {
        ...(emp?.planningSettings || {}),
        fixedShifts,
        isApprentice: emp?.isApprentice === true,
        schoolDays: Array.isArray(emp?.schoolDays) ? emp.schoolDays : [],
      }
    },
    rememberMonthlyPlanningSettings(emp, settings) {
      const allSettings = { ...(emp.shiftPlanningSettings || {}) }
      const studioSettings = { ...(allSettings[this.studioId] || {}) }
      studioSettings.monthlyOverrides = {
        ...(studioSettings.monthlyOverrides || {}),
        [this.planningMonthId]: { ...settings },
      }
      allSettings[this.studioId] = studioSettings
      this.$set(emp, "shiftPlanningSettings", allSettings)
    },
    setEmployeePlanningScope(emp, scope) {
      this.$set(emp, "_planningScope", scope)
      return this.saveSettings(emp)
    },
    adoptEmployeeStandardMonths(emp, request) {
      return this.saveSettings(emp, request)
    },
    async saveSettings(emp, adoptionRequest = null) {
      if (!emp?._id || !this.planningMonthId) return false
      const settings = JSON.parse(JSON.stringify(this.buildMonthlyPlanningSettings(emp)))
      const context = { studioId: this.studioId, month: this.planningMonthId, scope: emp._planningScope || "month" }
      const key = context.studioId + ":" + context.month + ":" + emp._id
      if (!this._planningSaveQueues) this._planningSaveQueues = Object.create(null)
      this.$set(emp, "_planningSettingsSaving", true)
      const previous = this._planningSaveQueues[key] || Promise.resolve()
      const job = previous.catch(() => {}).then(() => this.persistEmployeeSettings(emp, settings, context, adoptionRequest))
      this._planningSaveQueues[key] = job
      try { return await job } finally {
        if (this._planningSaveQueues[key] === job) {
          delete this._planningSaveQueues[key]
          this.$set(emp, "_planningSettingsSaving", false)
        }
      }
    },
    async persistEmployeeSettings(emp, settings, context, adoptionRequest) {
      if (this.studioId !== context.studioId || this.planningMonthId !== context.month) return false
      try {
        const request = adoptionRequest || (context.scope === "future"
          ? standardUpdateRequest(emp.shiftPlanningSettings?.[context.studioId] || {}, context.month, settings)
          : null)
        const saved = await api.shiftManagement.saveMonthlyPlanningSettings(emp._id, context.studioId, context.month, settings, request)
        if (saved.studioSettings) {
          this.$set(emp, "shiftPlanningSettings", { ...(emp.shiftPlanningSettings || {}), [context.studioId]: saved.studioSettings })
        } else {
          this.rememberMonthlyPlanningSettings(emp, saved.settings || settings)
        }
        this.$set(emp, "_monthlySettingsSaveFailed", false)
        if (this.isValidWorkHoursSummary(emp.hoursSummary)) this.$set(emp, "workHoursSummaryUnavailable", false)
      } catch (e) {
        console.error("Fehler beim Speichern der Monats-Planning-Settings:", e)
        this.$set(emp, "_monthlySettingsSaveFailed", true)
        this.$set(emp, "workHoursSummaryUnavailable", true)
        this.workHoursSummaryWarning =
          `Die Monatseinstellungen für ${emp.fullName || "diese Person"} konnten nicht gespeichert werden. ` +
          "Die automatische Zuweisung bleibt gesperrt."
        this.$toast.error(e?.response?.data?.message || e.message || "Monatseinstellungen konnten nicht gespeichert werden.")
        this.employees = [...this.employees]
        return false
      }
      if (emp.planningSettings.isReadyForPlanning && !this.isValidWorkHoursSummary(emp.hoursSummary)) {
        void this.refreshEmployeeWorkHoursSummary(emp)
      } else {
        this.refreshEmployeeWorkHoursSummaryIfNeeded(emp)
        const activeMissing = this.employees.filter(
          (employee) =>
            employee?.planningSettings?.isReadyForPlanning === true &&
            (employee.workHoursSummaryUnavailable === true || !this.isValidWorkHoursSummary(employee.hoursSummary)),
        ).length
        if (activeMissing === 0 && this.hasCompleteWorkHoursSummaries) {
          const inactiveMissing = this.employees.filter(
            (employee) => employee?.planningSettings?.isReadyForPlanning !== true && employee.workHoursSummaryUnavailable === true,
          ).length
          this.workHoursSummaryWarning = inactiveMissing
            ? `${inactiveMissing} inaktive Stundenübersicht${
                inactiveMissing === 1 ? "" : "en"
              } fehlt. Die aktiven Personen sind vollständig berechnet.`
            : ""
        }
      }
      this.employees = [...this.employees]
      return true
    },

    async updateEmployeeStatus(emp) {
      this.$set(emp.planningSettings, "isApprentice", emp.isApprentice === true)
      this.$set(emp.planningSettings, "schoolDays", Array.isArray(emp.schoolDays) ? emp.schoolDays : [])
      if (!emp.isApprentice) emp.planningSettings.longCommute = false
      await this.saveSettings(emp)
    },

    getStudioInfo(emp) {
      if (!emp.studio) return {}
      return emp.studio.find((s) => String(s._id) === String(this.studioId)) || {}
    },

    async updateStudioHideStatus(emp, hideVal) {
      const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
      if (!studioInfo) return

      const prevHide = studioInfo.hide
      const prevShow = studioInfo.showInCalendarAnyway

      studioInfo.hide = !!hideVal
      if (!hideVal) {
        studioInfo.showInCalendarAnyway = false
      }

      try {
        const profileStatus = emp._profilePlanningStatus || {}
        const payload = {
          isApprentice: profileStatus.isApprentice === true,
          schoolDays: Array.isArray(profileStatus.schoolDays) ? profileStatus.schoolDays : [],
          cannotOpenOrCloseShift: profileStatus.cannotOpenOrCloseShift === true,
          studioId: this.studioId,
          hide: studioInfo.hide,
          showInCalendarAnyway: studioInfo.showInCalendarAnyway,
        }
        await api.shiftManagement.updateEmployeePlanningSettings(emp._id, payload)
        this.$toast.success("Mitarbeiter-Status aktualisiert")
      } catch (e) {
        console.error(e)
        studioInfo.hide = prevHide
        studioInfo.showInCalendarAnyway = prevShow
        this.$toast.error("Fehler beim Speichern des Status")
      }
      this.employees = [...this.employees]
    },

    async updateStudioShowInCalendarAnyway(emp, showVal) {
      const studioInfo = emp.studio?.find((s) => String(s._id) === String(this.studioId))
      if (!studioInfo) return

      const prevShow = studioInfo.showInCalendarAnyway
      studioInfo.showInCalendarAnyway = !!showVal

      try {
        const profileStatus = emp._profilePlanningStatus || {}
        const payload = {
          isApprentice: profileStatus.isApprentice === true,
          schoolDays: Array.isArray(profileStatus.schoolDays) ? profileStatus.schoolDays : [],
          cannotOpenOrCloseShift: profileStatus.cannotOpenOrCloseShift === true,
          studioId: this.studioId,
          hide: studioInfo.hide,
          showInCalendarAnyway: studioInfo.showInCalendarAnyway,
        }
        await api.shiftManagement.updateEmployeePlanningSettings(emp._id, payload)
        this.$toast.success("Mitarbeiter-Status aktualisiert")
      } catch (e) {
        console.error(e)
        studioInfo.showInCalendarAnyway = prevShow
        this.$toast.error("Fehler beim Speichern des Status")
      }
      this.employees = [...this.employees]
    },

    async markAllReady() {
      await Promise.all(
        this.employees.map((e) => {
          e.planningSettings.isReadyForPlanning = true
          return this.saveSettings(e)
        }),
      )
    },

    getCentralHoursTotal(emp) {
      return emp && emp.hoursSummary && emp.hoursSummary.total ? emp.hoursSummary.total : null
    },
    formatHoursOrDash(value) {
      return Number.isFinite(Number(value)) && value !== null ? `${Number(value).toFixed(1)}h` : "–"
    },

    calculateContractTarget(emp) {
      const total = this.getCentralHoursTotal(emp)
      if (!total) return null
      const centralValue = Number(total && total.grossTargetHours)
      if (Number.isFinite(centralValue)) return Math.max(0, centralValue)
      return null
    },

    calculateVacationHours(emp) {
      const value = Number(this.getCentralHoursTotal(emp)?.vacationHours)
      return Number.isFinite(value) ? Math.max(0, value) : 0
    },

    calculateAbsenceHours(emp) {
      const value = Number(this.getCentralHoursTotal(emp)?.absenceHours)
      return Number.isFinite(value) ? Math.max(0, value) : 0
    },

    calculateShiftCapacity(emp) {
      const centralValue = Number(this.getCentralHoursTotal(emp)?.netTargetHours)
      if (Number.isFinite(centralValue)) return Math.max(0, centralValue)
      return null
    },

    calculateSchoolHours(emp) {
      if (!emp.isApprentice || !emp.schoolDays || emp.schoolDays.length === 0) return 0

      let schoolDaysCount = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)
      let current = start.clone()

      while (current.isSameOrBefore(end)) {
        const dayIso = current.isoWeekday()
        const isSchoolDay = emp.schoolDays.some((sd) => sd.dayOfWeek === dayIso)
        const isHoliday = (this.schoolHolidays || []).some(
          (sh) => sh.startDate && sh.endDate && current.isBetween(sh.startDate, sh.endDate, null, "[]"),
        )

        if (isSchoolDay && !isHoliday) {
          schoolDaysCount++
        }
        current.add(1, "day")
      }

      return schoolDaysCount * 8.4
    },

    calculateIndividualTarget(emp) {
      return this.calculateContractTarget(emp)
    },

    calculateDemand() {
      if (!this.weeklyTemplateData || this.weeklyTemplateData.length === 0) {
        this.totalDemandHours = 0
        return
      }

      this.calcStats = { standardHours: 0, removedHours: 0, specialHours: 0, specialEvents: [] }

      const specialDaysMap = {}
      this.specialShifts.forEach((s) => {
        if (!specialDaysMap[s.date]) {
          const isReplace = s.allocationNote && s.allocationNote.includes("Ersatz")
          specialDaysMap[s.date] = { mode: isReplace ? "replace" : "add", shifts: [] }
        }
        specialDaysMap[s.date].shifts.push(s)
      })

      let totalHours = 0
      const start = this.$moment(this.planningStartDate)
      const end = this.$moment(this.planningEndDate)

      let current = start.clone()
      while (current.isSameOrBefore(end, "day")) {
        const dateStr = current.format("YYYY-MM-DD")
        const dayOfWeekISO = current.isoWeekday()

        const specialConfig = specialDaysMap[dateStr]
        const isReplace = specialConfig && specialConfig.mode === "replace"

        const dayTemplate = this.weeklyTemplateData.find((d) => d.dayOfWeek === dayOfWeekISO)
        let dailyStandardHours = 0

        if (dayTemplate && dayTemplate.requiredShifts) {
          dayTemplate.requiredShifts.forEach((req) => {
            const tpl = req.shiftTemplateId
            if (tpl && tpl.times) {
              tpl.times.forEach((time) => {
                if (time.type === "Arbeitszeit" && time.start && time.end) {
                  const s = this.$moment(time.start, "HH:mm")
                  const e = this.$moment(time.end, "HH:mm")
                  if (e.isBefore(s)) e.add(1, "day")
                  dailyStandardHours += e.diff(s, "hours", true)
                }
              })
            }
          })
        }

        if (isReplace) {
          this.calcStats.removedHours += dailyStandardHours
        } else {
          this.calcStats.standardHours += dailyStandardHours
          totalHours += dailyStandardHours
        }

        if (specialConfig) {
          let dailySpecialHours = 0
          specialConfig.shifts.forEach((shift) => {
            const times = shift.times || []
            times.forEach((time) => {
              if (time.type === "Arbeitszeit" && time.start && time.end) {
                const s = this.$moment(time.start, "HH:mm")
                const e = this.$moment(time.end, "HH:mm")
                if (e.isBefore(s)) e.add(1, "day")
                dailySpecialHours += e.diff(s, "hours", true)
              }
            })
          })

          this.calcStats.specialHours += dailySpecialHours
          totalHours += dailySpecialHours

          if (!this.calcStats.specialEvents.find((e) => e.date === dateStr)) {
            this.calcStats.specialEvents.push({
              date: dateStr,
              mode: specialConfig.mode,
              shifts: specialConfig.shifts.length,
            })
          }
        }

        current.add(1, "day")
      }
      this.totalDemandHours = totalHours
    },
    // Ersetze die komplette Methode 'triggerAssignment' hiermit:
    async triggerAssignment() {
      if (this.loading || !Array.isArray(this.employees) || this.employees.length === 0) {
        this.$toast.info("Die Mitarbeiterdaten werden noch geladen.")
        return
      }
      if (this.readyCount === 0) {
        this.$toast.info("Aktiviere mindestens eine Person für die Planung.")
        return
      }
      if (!this.hasCompleteWorkHoursSummaries) {
        this.$toast.warning("Die automatische Zuweisung wartet auf eine vollständige Sollstunden-Berechnung.")
        return
      }

      // Alle finalen Monatswerte müssen serverseitig bestätigt sein, bevor
      // der Assistent mit ihnen arbeitet.
      const saveResults = await Promise.all(this.employees.map((emp) => this.saveSettings(emp)))
      if (saveResults.some((saved) => saved !== true)) return

      // 2. Prüfung: Sind alle bereit?
      const status = this.getConfirmationStatus()
      if (!status.allMarked) {
        // Falls nicht alle bereit sind, warnen wir kurz, lassen es aber zu
        // (der Parent Dialog zeigt dann meist noch den Bestätigungs-Dialog)
        this.$toast.info("Hinweis: Nicht alle Mitarbeiter sind auf 'Bereit' gesetzt.")
      }

      // 3. Signal an Parent senden: "Weiter zu Schritt 4"
      // Wir rufen keine API mehr hier auf! Das macht der 'Assignment'-Schritt.
      this.$emit("next")
    },
    getConfirmationStatus() {
      const notReady = this.employees.filter((e) => !e.planningSettings.isReadyForPlanning)
      return {
        allMarked: notReady.length === 0,
        notReadyNames: notReady.map((e) => e.fullName),
        readyEmployeeIds: this.employees
          .filter((e) => e.planningSettings.isReadyForPlanning)
          .map((e) => String(e.user?._id || e.user || ""))
          .filter(Boolean),
      }
    },
    otherEmployees(currentEmp) {
      const currentUserId = String(currentEmp.user?._id || currentEmp.user)
      return this.employees
        .filter((e) => String(e.user?._id || e.user) !== currentUserId)
        .map((e) => ({
          userId: String(e.user?._id || e.user),
          fullName: e.fullName || "Mitarbeiter",
        }))
    },
    isExitInMonth(emp) {
      if (!emp.exitDate) return false
      const exitM = this.$moment(emp.exitDate)
      const startM = this.$moment(this.planningStartDate)
      const endM = this.$moment(this.planningEndDate)
      return exitM.isBetween(startM, endM, "day", "[]")
    },
    calculateFullMonthSoll(emp) {
      return this.calculateContractTarget(emp)
    },
    calculateCappedSoll(emp) {
      return this.calculateShiftCapacity(emp)
    },
    toggleOvertimeOffset(emp) {
      if (emp.planningSettings.offsetOvertime) {
        emp.planningSettings.customSollAdjustment = -Math.round(emp.overTime || 0)
      } else {
        emp.planningSettings.customSollAdjustment = 0
      }
      this.saveSettings(emp)
    },
    getPlanningPercentage(studioInfo, planningSettings) {
      if (!studioInfo) return 0
      const planningPercentages = planningSettings?.percentages
      if (planningPercentages && planningPercentages[studioInfo._id] != null && planningPercentages[studioInfo._id] !== "") {
        const monthlyPercentage = Number(String(planningPercentages[studioInfo._id]).replace(",", "."))
        if (Number.isFinite(monthlyPercentage)) return Math.max(0, monthlyPercentage)
      }

      if (studioInfo.percentage === undefined || studioInfo.percentage === null || studioInfo.percentage === "") return 0
      const statisticPercentage = Number(String(studioInfo.percentage).replace(",", "."))
      return Number.isFinite(statisticPercentage) ? Math.max(0, statisticPercentage) : 0
    },
  },
}
</script>

<style scoped>
.emp-settings-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}
.emp-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.emp-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  display: flex;
  align-items: center;
  justify-content: center;
}
.emp-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.emp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}
.emp-panels {
  background: transparent !important;
}
.emp-panel {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 12px !important;
  margin-bottom: 8px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}
.emp-panel:hover {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background: rgba(255, 255, 255, 0.05) !important;
}
.emp-bottom {
  flex-shrink: 0;
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.emp-bottom-inner {
  display: flex;
  align-items: center;
  gap: 20px;
}
.emp-coverage {
  flex: 1;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 8px;
  transition: background 0.2s;
}
.emp-coverage:hover {
  background: rgba(255, 255, 255, 0.05);
}
.emp-progress-wrap {
  display: flex;
  align-items: center;
}
.emp-progress-bg {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.emp-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease;
}
.emp-progress-fill.complete {
  background: linear-gradient(90deg, #43a047, #66bb6a);
}
.emp-progress-fill.partial {
  background: linear-gradient(90deg, #fb8c00, #ffa726);
}
.emp-progress-fill.low {
  background: linear-gradient(90deg, #e53935, #ef5350);
}
.emp-submit-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 0 24px !important;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3) !important;
  flex-shrink: 0;
}
.border-right-custom {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}
@media (max-width: 960px) {
  .border-right-custom {
    border-right: none;
  }
  .emp-bottom-inner {
    flex-direction: column;
    gap: 12px;
  }
}
.hover-card:hover {
  border-color: #4caf50 !important;
}
.hover-row:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.border-top {
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.border-left-blue {
  border-left: 3px solid #2196f3;
}
.border-left-orange {
  border-left: 3px solid #ffa726;
}
.hover-bg:hover {
  background-color: rgba(255, 255, 255, 0.04);
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-help {
  cursor: help;
}
</style>
